import ModalDetalheHistorico from '@/modules/system/historico/ModalDetalheHistorico';
import { rotuloAcao, rotuloTabela } from '@/modules/system/historico/historico-detalhe.utils';
import { IconeAcao, IconesCampos, LegendaIconesHistorico } from '@/modules/system/historico/historico-icones';
import { Column, DataTable } from '@/shared/components/tables/DataTable';
import { DataAmigavel } from '@/shared/utils/datas';
import { GetHistoricoUsuario } from '@/modules/system/historico/historico.service';
import { GetUsuarios } from '@/modules/system/usuario/usuario.service';
import { Link, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { Tabelas, getEditLink } from '@/shared/constants/tables';
import { ptBR } from 'date-fns/locale/pt-BR';
import { toast } from 'react-toastify';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { useContext, useEffect, useState } from 'react';
import MyContext from '@/shared/contexts/AppContext';
import { getUserIdFromLoginJwt } from '@/modules/system/auth/application/auth.service';
import { FaEye } from 'react-icons/fa';
import { HistoricoDto, ReadUsuarioDto } from '@/api.types';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { BiArrowBack } from 'react-icons/bi';
import Links from '@/shared/constants/navegacao';

function linkDoRegistro(row: HistoricoDto, usuarios: ReadUsuarioDto[]): string | null {
  const direto = getEditLink(row.tabela, row.registro);
  if (direto) return direto;

  if (row.tabela === 'usuario') {
    const pessoaId = usuarios.find((usuario) => String(usuario.id) === String(row.registro))?.pessoa?.id;
    if (pessoaId) return `${Links.Pessoa_Editar}/${pessoaId}`;
  }

  const diff = row.diff as Record<string, unknown> | null | undefined;
  const pessoa = diff?.pessoa_id;
  const pessoaId =
    typeof pessoa === 'string'
      ? pessoa
      : pessoa && typeof pessoa === 'object' && typeof (pessoa as { depois?: unknown }).depois === 'string'
        ? (pessoa as { depois: string }).depois
        : null;

  if (pessoaId) return `${Links.Pessoa_Editar}/${pessoaId}`;
  return null;
}

function periodoPadrao() {
  const hoje = new Date();
  return {
    inicio: new Date(hoje.getFullYear(), hoje.getMonth(), 1),
    fim: new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0),
  };
}

function dataDaUrl(valor: string | null, fallback: Date) {
  if (!valor || !/^\d{4}-\d{2}-\d{2}$/.test(valor)) return fallback;
  const [ano, mes, dia] = valor.split('-').map(Number);
  return new Date(ano, mes - 1, dia);
}

function dataParaUrl(data: Date) {
  if (!(data instanceof Date) || Number.isNaN(data.getTime())) return '';
  const mes = String(data.getMonth() + 1).padStart(2, '0');
  const dia = String(data.getDate()).padStart(2, '0');
  return `${data.getFullYear()}-${mes}-${dia}`;
}

const ACOES_FILTRO = [
  { valor: 'INSERT', rotulo: 'Inclusão' },
  { valor: 'UPDATE', rotulo: 'Atualização' },
  { valor: 'DELETE', rotulo: 'Exclusão' },
] as const;

type AcaoFiltro = (typeof ACOES_FILTRO)[number]['valor'];

function acoesDaUrl(valor: string | null): AcaoFiltro[] {
  if (!valor) return ACOES_FILTRO.map((item) => item.valor);
  if (valor === '-') return [];
  const validas = new Set<string>(ACOES_FILTRO.map((item) => item.valor));
  return valor.split(',').map((item) => item.trim()).filter((item): item is AcaoFiltro => validas.has(item));
}

function acaoParaFiltro(acoes: AcaoFiltro[]) {
  if (acoes.length === ACOES_FILTRO.length) return undefined;
  return acoes.join(',') || '-';
}

export default function HistoricoIndexPage() {
  const context = useContext(MyContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const { setActions } = useBreadcrumbActions();
  const padrao = periodoPadrao();

  const [idUsuario, setIdUsuario] = useState(() => Number(searchParams.get('usuario')) || 0);
  const [dataInicial, setDataInicial] = useState(() => dataDaUrl(searchParams.get('inicio'), padrao.inicio));
  const [dataFinal, setDataFinal] = useState(() => dataDaUrl(searchParams.get('fim'), padrao.fim));
  const [textoBusca, setTextoBusca] = useState('');
  const [tabela, setTabela] = useState(() => searchParams.get('tabela') ?? '');
  const [acoes, setAcoes] = useState<AcaoFiltro[]>(() => acoesDaUrl(searchParams.get('acao')));
  const [loaded, setLoaded] = useState(false)
  const [buscando, setBuscando] = useState(false)

  const [listaUsuarios, setListaUsuarios] = useState<ReadUsuarioDto[]>([]);
  const [registros, setRegistros] = useState<[HistoricoDto[], number]>();
  const [detalhe, setDetalhe] = useState<HistoricoDto | null>(null);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const tableState = useDataTableState('criadoEm', undefined, 'DESC', { syncPageToUrl: false });

  useEffect(() => {
    setActions({
      primary: {
        label: 'Nova Pesquisa',
        variant: 'primary',
        onClick: () => setFiltrosAbertos(true),
      },
      secondary: {
        label: 'Voltar para Auditorias',
        href: Links.Auditorias_Index,
        icon: BiArrowBack,
        onClick: () => {},
      },
    });
    return () => setActions({});
  }, [setActions]);

  useEffect(() => {
    const usuarioNaUrl = searchParams.has('usuario');
    const idLogado = context?.state.userId ?? getUserIdFromLoginJwt();
    const usernameLogado = context?.state.username?.trim().toLowerCase();
    GetUsuarios({ skip: 0, take: 1000 })
      .then((result) => {
        const ordenados = result[0].sort((a, b) => a.username!.localeCompare(b.username!));
        setListaUsuarios(ordenados);
        if (usuarioNaUrl || ordenados.length === 0) return;
        const porId = idLogado != null ? ordenados.find((usuario) => usuario.id === idLogado) : undefined;
        const porNome = !porId && usernameLogado
          ? ordenados.find((usuario) => usuario.username?.toLowerCase() === usernameLogado)
          : undefined;
        const escolhido = porId ?? porNome ?? ordenados[0];
        setIdUsuario(escolhido.id);
      })
      .catch(console.warn)
      .finally(() => {
        setLoaded(true)
      })
  }, []);

  function handleBuscar() {
    if (loaded) {

      setBuscando(true)
      const nomeUsuario = listaUsuarios.find(u => u.id === idUsuario)?.pessoa?.nome;
      setTextoBusca(`Log de: ${nomeUsuario} no período de ${DataAmigavel(dataInicial)} à ${DataAmigavel(dataFinal)}.`)

      const filters = {
        criadopor: idUsuario,
        data_inicial: dataParaUrl(dataInicial),
        data_final: dataParaUrl(dataFinal),
        skip: (tableState.currentPage - 1) * tableState.pageSize,
        take: tableState.pageSize,
        tabela: tabela,
        acao: acaoParaFiltro(acoes),
        orderBy: tableState.sortConfig.direction,
        orderField: tableState.sortConfig.key
      }

      GetHistoricoUsuario(filters)
        .then((result) => {
          const [, total] = result;
          const maxPage = Math.max(1, Math.ceil((total || 0) / tableState.pageSize));
          if (tableState.currentPage > maxPage) {
            tableState.setCurrentPage(maxPage);
            return;
          }
          setRegistros(result);
        })
        .catch((res) => {
          toast.error(res)
        })
        .finally(() => {
          setBuscando(false)
        })
    }
  }

  function pesquisar() {
    setFiltrosAbertos(false);
    if (tableState.currentPage !== 1) {
      tableState.setCurrentPage(1);
      return;
    }
    handleBuscar();
  }

  useEffect(() => {
    if (!loaded) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(tableState.currentPage));
      next.set('usuario', idUsuario > 0 ? String(idUsuario) : '');
      if (tabela) next.set('tabela', tabela);
      else next.delete('tabela');
      const acaoUrl = acaoParaFiltro(acoes);
      if (acaoUrl) next.set('acao', acaoUrl);
      else next.delete('acao');
      next.delete('ocultar_logins');
      next.delete('ocultar_login');
      const inicio = dataParaUrl(dataInicial);
      const fim = dataParaUrl(dataFinal);
      if (inicio) next.set('inicio', inicio);
      else next.delete('inicio');
      if (fim) next.set('fim', fim);
      else next.delete('fim');
      return next;
    }, { replace: true });
  }, [loaded, idUsuario, tabela, acoes, dataInicial, dataFinal, tableState.currentPage, setSearchParams]);

  const colunas: Column<HistoricoDto>[] = [
    { key: "criadoEm", header: "Data", render: (row) => format(new Date(row.criadoEm), "dd/MM/yyyy HH:mm", { locale: ptBR }) },
    {
      key: "acao",
      header: "Ação",
      render: (row) => (
        <span className="d-inline-flex align-items-center gap-2">
          <IconeAcao acao={row.acao} />
          {row.acao === 'UPDATE'
            ? <IconesCampos diff={row.diff as Record<string, unknown> | null} />
            : rotuloAcao(row.acao)}
        </span>
      ),
    },
    { key: "tabela", header: "Tabela", render: (row) => rotuloTabela(row.tabela) },
    { key: "criadoPor.username", header: "Usuário" },
    {
      key: "registro",
      header: "ID",
      render: (row) => {
        const destino = linkDoRegistro(row, listaUsuarios);
        if (!destino) return row.registro;
        return (
          <Link className="link-secondary" to={destino} target="_blank" rel="noopener noreferrer">
            {row.registro}
          </Link>
        );
      },
    },
    {
      key: "acoes",
      header: "Detalhes",
      render: (row) => (
        <button
          type="button"
          className="btn btn-link link-secondary p-0"
          title="Ver detalhes"
          onClick={() => setDetalhe(row)}
        >
          <FaEye size={24} color="#0daaaf" />
        </button>
      ),
    },
  ];

  const opcoesUsuario = listaUsuarios.map((item) => ({
    value: item.id,
    label: item.username ?? String(item.id),
  }));
  const usuarioSelecionado = opcoesUsuario.find((opcao) => opcao.value === idUsuario) ?? null;

  return (
    <div className="row">
      <div className="col-12 d-flex justify-content-between align-items-start gap-3 flex-wrap">
        <div>
          <h3>Logs de Uso</h3>
          <span className="blockquote-footer pb-4">
            Log de Alterações do Sistema
          </span>
        </div>
        <LegendaIconesHistorico />
      </div>
      <hr />

      {registros &&
        <>
          {textoBusca && <p className="text-muted small mb-2">{textoBusca}</p>}
          <DataTable
            columns={colunas}
            data={registros?.[0]}
            onChange={handleBuscar}
            serverSide={true}
            showFilter={false}
            states={tableState}
            totalRecords={registros?.[1]}
          />
        </>
      }
      <Modal show={filtrosAbertos} onHide={() => setFiltrosAbertos(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Nova Pesquisa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-column gap-3">
            <div>
              <label htmlFor="usuario" className="form-label">Usuário</label>
              <Select
                inputId="usuario"
                classNamePrefix="react-select"
                options={opcoesUsuario}
                value={usuarioSelecionado}
                placeholder="Selecione um usuário..."
                isClearable
                isSearchable
                noOptionsMessage={() => 'Nenhum usuário encontrado'}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                onChange={(opcao) => setIdUsuario(opcao?.value ?? 0)}
              />
            </div>
            <div>
              <label htmlFor="tabela" className="form-label">Tabela</label>
              <select
                id="tabela"
                title="Tabela"
                className="form-select"
                value={tabela}
                onChange={(e) => { setTabela(e.target.value) }}
              >
                <option value="">-</option>
                {Object.entries(Tabelas).map((item) => (
                  <option key={item[0]} value={item[0]}>
                    {item[1]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <span className="form-label d-block">Ação</span>
              <div className="d-flex flex-wrap gap-3">
                {ACOES_FILTRO.map((item) => (
                  <div className="form-check mb-0" key={item.valor}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`acao-${item.valor}`}
                      checked={acoes.includes(item.valor)}
                      onChange={() => setAcoes((atual) => (
                        atual.includes(item.valor)
                          ? atual.filter((acao) => acao !== item.valor)
                          : [...atual, item.valor]
                      ))}
                    />
                    <label className="form-check-label d-inline-flex align-items-center gap-1" htmlFor={`acao-${item.valor}`}>
                      <IconeAcao acao={item.valor} />
                      {item.rotulo}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="row g-2">
              <div className="col-6">
                <label htmlFor="data_inicial" className="form-label">Data inicial</label>
                <input
                  id="data_inicial"
                  type="date"
                  className="form-control form-control-sm"
                  style={{ minWidth: 0 }}
                  value={dataParaUrl(dataInicial)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDataInicial(dataDaUrl(e.target.value, dataInicial))}
                />
              </div>
              <div className="col-6">
                <label htmlFor="data_final" className="form-label">Data final</label>
                <input
                  id="data_final"
                  type="date"
                  className="form-control form-control-sm"
                  style={{ minWidth: 0 }}
                  value={dataParaUrl(dataFinal)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDataFinal(dataDaUrl(e.target.value, dataFinal))}
                />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setFiltrosAbertos(false)}>
            Fechar
          </button>
          <button type="button" className="btn btn-outline-success" onClick={pesquisar} disabled={buscando}>
            {buscando ? 'Buscando...' : 'Pesquisar'}
          </button>
        </Modal.Footer>
      </Modal>
      <ModalDetalheHistorico
        item={detalhe}
        show={detalhe != null}
        onHide={() => setDetalhe(null)}
      />
    </div>
  );
}
